import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Box,
  Text,
  Group,
  Stack,
  ActionIcon,
  Modal,
  TextInput,
  Button,
  useMantineTheme,
  useMantineColorScheme,
  Paper,
  Tooltip,
  Badge,
  Flex,
} from "@mantine/core";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import { useRooms } from "@/hooks";
import { Room, EventTeamInput } from "@/utils";

import { AppIcon } from "@/components/generic";
import { RoomSelector } from "../Rooms";

export interface EventTeamSelectorProps {
  /** Selected event teams */
  value: EventTeamInput[] | null | undefined;

  /** Callback when event teams change */
  onChange?: (eventTeams: EventTeamInput[]) => void;

  /** Venue ID to filter rooms by */
  venueId?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";

  /** Maximum height for the teams area */
  maxHeight?: number;
}

// ─── ID helpers ──────────────────────────────────────────────────────────────

const generateTempId = (): string =>
  `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

const isTempId = (id?: string): boolean => id?.startsWith("temp_") ?? false;

// ─── Draft row type (used only inside the modal) ─────────────────────────────

interface DraftRow {
  /** Stable key for React / DnD */
  draftId: string;
  /** Real backend ID if the team already exists */
  backendId?: string;
  teamName: string;
  supportingRooms: string[];
}

const emptyDraftRow = (): DraftRow => ({
  draftId: generateTempId(),
  teamName: "",
  supportingRooms: [],
});

// ─── Helper: convert saved teams → draft rows ────────────────────────────────

const teamsToDraftRows = (teams: EventTeamInput[]): DraftRow[] => {
  const rows: DraftRow[] = teams.map((team) => ({
    draftId: generateTempId(),
    backendId: team.id && !isTempId(team.id) ? team.id : undefined,
    teamName: team.teamName || "",
    // Defensive copy — never share array references between rows
    supportingRooms: [
      ...(team.supportingRooms?.map((r) => r || "").filter(Boolean) || []),
    ],
  }));
  // Always append one blank trailing row
  rows.push(emptyDraftRow());
  return rows;
};

// ─── Helper: convert draft rows → EventTeamInput[] (strip empty + temp IDs) ──

const draftRowsToTeams = (rows: DraftRow[]): EventTeamInput[] =>
  rows
    .filter((row) => row.teamName.trim() !== "")
    .map((row) => {
      const team: EventTeamInput = {
        teamName: row.teamName.trim(),
        // Defensive copy so each team gets its own array
        supportingRooms: [...row.supportingRooms],
      };
      if (row.backendId) {
        team.id = row.backendId;
      }
      return team;
    });

// ─── Component ───────────────────────────────────────────────────────────────

export const EventTeamSelector: React.FC<EventTeamSelectorProps> = ({
  value,
  onChange,
  venueId,
  placeholder = "No teams added yet",
  error,
  disabled,
  size = "sm",
}) => {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  // ── Room data for the read-only summary ──────────────────────────────────
  const { getMany } = useRooms({ autoLoad: false });
  const [roomsMap, setRoomsMap] = useState<Map<string, Room>>(new Map());
  const [loadingRooms, setLoadingRooms] = useState(false);

  const teams = useMemo(() => {
    return (value || []).map((team) => ({
      ...team,
      id: team.id || generateTempId(),
    }));
  }, [value]);

  const allRoomIds = useMemo(() => {
    const ids = new Set<string>();
    teams.forEach((team) =>
      team.supportingRooms?.forEach((id) => id && ids.add(id))
    );
    return Array.from(ids);
  }, [teams]);

  useEffect(() => {
    if (allRoomIds.length === 0) {
      setRoomsMap(new Map());
      return;
    }
    setLoadingRooms(true);
    getMany(allRoomIds)
      .then((rooms: Room[]) => {
        const map = new Map<string, Room>();
        rooms.forEach((r: Room) => r.id && map.set(r.id, r));
        setRoomsMap(map);
      })
      .catch(() => { })
      .finally(() => setLoadingRooms(false));
  }, [allRoomIds, getMany]);

  const getRoomDisplayName = useCallback(
    (roomId: string): string => {
      const room = roomsMap.get(roomId);
      return room?.roomNumber || room?.name || `Room ${roomId}`;
    },
    [roomsMap]
  );

  // ── Modal state ──────────────────────────────────────────────────────────
  const [modalOpened, setModalOpened] = useState(false);
  const [draftRows, setDraftRows] = useState<DraftRow[]>([emptyDraftRow()]);

  const openModal = useCallback(() => {
    setDraftRows(
      teams.length > 0 ? teamsToDraftRows(teams) : [emptyDraftRow()]
    );
    setModalOpened(true);
  }, [teams]);

  const closeModal = useCallback(() => {
    setModalOpened(false);
  }, []);

  // ── Draft row mutations ──────────────────────────────────────────────────

  const handleTeamNameChange = useCallback(
    (draftId: string, newName: string) => {
      setDraftRows((prev) => {
        const idx = prev.findIndex((r) => r.draftId === draftId);
        if (idx === -1) return prev;

        const updated = prev.map((row) =>
          row.draftId === draftId ? { ...row, teamName: newName } : row
        );

        const wasEmpty = prev[idx].teamName === "";
        const nowHasValue = newName !== "";
        const isLastRow = idx === prev.length - 1;

        // Auto-grow: if this was the last (trailing) row and now has a value, append a new blank
        if (wasEmpty && nowHasValue && isLastRow) {
          return [...updated, emptyDraftRow()];
        }

        return updated;
      });
    },
    []
  );

  const handleRemoveRow = useCallback(
    (draftId: string) => {
      setDraftRows((prev) => {
        // If only one row, just clear it
        if (prev.length === 1) {
          return [emptyDraftRow()];
        }
        const filtered = prev.filter((r) => r.draftId !== draftId);
        // Ensure there's always a trailing blank row
        const lastRow = filtered[filtered.length - 1];
        if (lastRow && lastRow.teamName.trim() !== "") {
          return [...filtered, emptyDraftRow()];
        }
        return filtered;
      });
    },
    []
  );

  const handleRoomsChange = useCallback(
    (draftId: string, roomIds: string | string[] | null) => {
      // Always create a fresh array — never mutate or share references
      const selected = Array.isArray(roomIds) ? [...roomIds] : [];
      setDraftRows((prev) =>
        prev.map((row) =>
          row.draftId === draftId ? { ...row, supportingRooms: selected } : row
        )
      );
    },
    []
  );

  // ── Drag & drop ──────────────────────────────────────────────────────────

  const handleDragEnd = useCallback((result: any) => {
    if (!result.destination) return;
    setDraftRows((prev) => {
      const items = Array.from(prev);
      const [moved] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, moved);
      return items;
    });
  }, []);

  // ── Confirm ──────────────────────────────────────────────────────────────

  const handleConfirm = useCallback(() => {
    const cleanedTeams = draftRowsToTeams(draftRows);
    onChange?.(cleanedTeams);
    setModalOpened(false);
  }, [draftRows, onChange]);

  // ── Derived display values ───────────────────────────────────────────────

  const teamCount = teams.length;

  const dimensions = useMemo(() => {
    const map = {
      xs: { iconSize: 14, spacing: "xs" as const },
      sm: { iconSize: 16, spacing: "xs" as const },
      md: { iconSize: 18, spacing: "sm" as const },
      lg: { iconSize: 20, spacing: "sm" as const },
      xl: { iconSize: 24, spacing: "md" as const },
    };
    return map[size] ?? map.sm;
  }, [size]);

  const containerBorderColor = error
    ? theme.colors.red[4]
    : theme.colors.gray[4];

  const containerBg =
    colorScheme === "dark" ? theme.colors.dark[6] : theme.colors.gray[0];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <>
      {/* ── Read-only summary box ── */}
      <Box
        style={{
          borderRadius: theme.radius.sm,
          border: `1px solid ${containerBorderColor}`,
          backgroundColor: containerBg,
        }}
      >
        {teamCount > 0 ? (
          <Stack gap="xs" p={dimensions.spacing}>
            {teams.map((team, index) => (
              <Paper key={team.id} p="sm" withBorder>
                <Group gap="sm" wrap="nowrap">
                  <AppIcon iconName="Users" size={dimensions.iconSize} />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Group gap="xs" align="center">
                      <Text size="sm" fw={500} truncate>
                        {team.teamName || "Untitled Team"}
                      </Text>
                      <Badge size="xs" variant="outline">
                        #{index + 1}
                      </Badge>
                    </Group>
                    {team.supportingRooms && team.supportingRooms.length > 0 && (
                      <Flex gap={4} wrap="wrap" mt={4}>
                        {team.supportingRooms.map((roomId, idx) => (
                          <Badge
                            key={roomId || idx}
                            size="xs"
                            variant="light"
                            color="blue"
                          >
                            {loadingRooms
                              ? "…"
                              : getRoomDisplayName(roomId || "")}
                          </Badge>
                        ))}
                      </Flex>
                    )}
                  </Box>
                </Group>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Group gap={dimensions.spacing} p={dimensions.spacing}>
            <Text size={size} c="dimmed">
              {placeholder}
            </Text>
          </Group>
        )}

        {/* ── Manage button ── */}
        {!disabled && (
          <Group
            justify="center"
            p="xs"
            style={{ borderTop: `1px solid ${theme.colors.gray[3]}` }}
          >
            <Button
              size="xs"
              variant="light"
              leftSection={<AppIcon iconName="Settings" size={14} />}
              onClick={openModal}
            >
              {teamCount > 0 ? `Manage Teams (${teamCount})` : "Manage Teams"}
            </Button>
          </Group>
        )}
      </Box>

      {/* ── Error ── */}
      {error && (
        <Text size="sm" c="red" mt="xs">
          {error}
        </Text>
      )}

      {/* ── Manage Teams Modal ── */}
      <Modal
        opened={modalOpened}
        onClose={closeModal}
        title="Manage Teams"
        size="lg"
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Enter team names below. Select rooms for each team as needed. Empty rows are ignored on save.
          </Text>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="manage-teams-list">
              {(provided: any) => (
                <Stack
                  gap="xs"
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                >
                  {draftRows.map((row, index) => {
                    const isTrailingEmpty =
                      row.teamName.trim() === "" &&
                      index === draftRows.length - 1;

                    return (
                      <Draggable
                        key={row.draftId}
                        draggableId={row.draftId}
                        index={index}
                        isDragDisabled={isTrailingEmpty}
                      >
                        {(provided: any, snapshot: any) => (
                          <Paper
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            withBorder
                            p="sm"
                            style={{
                              ...provided.draggableProps.style,
                              backgroundColor: snapshot.isDragging
                                ? colorScheme === "dark"
                                  ? theme.colors.dark[5]
                                  : theme.colors.blue[0]
                                : undefined,
                              opacity: isTrailingEmpty ? 0.6 : 1,
                            }}
                          >
                            <Group gap="xs" wrap="nowrap" align="flex-start">
                              {/* Drag handle — hidden for trailing empty row */}
                              <Box
                                {...provided.dragHandleProps}
                                style={{
                                  cursor: isTrailingEmpty ? "default" : "grab",
                                  paddingTop: 6,
                                  visibility: isTrailingEmpty
                                    ? "hidden"
                                    : "visible",
                                }}
                              >
                                <AppIcon iconName="GripVertical" size={16} />
                              </Box>

                              {/* Row number */}
                              <Text
                                size="xs"
                                c="dimmed"
                                style={{ paddingTop: 8, minWidth: 18 }}
                              >
                                {isTrailingEmpty ? "+" : index + 1}
                              </Text>

                              {/* Team name + rooms stacked */}
                              <Stack gap="xs" style={{ flex: 1 }}>
                                <TextInput
                                  placeholder={
                                    isTrailingEmpty
                                      ? "Add another team…"
                                      : "Team name"
                                  }
                                  value={row.teamName}
                                  onChange={(e) =>
                                    handleTeamNameChange(
                                      row.draftId,
                                      e.target.value
                                    )
                                  }
                                  size="sm"
                                />

                                {/* Room selector — always visible for non-trailing rows */}
                                {!isTrailingEmpty && venueId && (
                                  <RoomSelector
                                    placeholder="Select rooms (optional)"
                                    venueId={venueId}
                                    value={row.supportingRooms}
                                    onChange={(roomIds: string | string[] | null, _?: Room | Room[]) =>
                                      handleRoomsChange(row.draftId, roomIds)
                                    }
                                    clearable
                                    size="sm"
                                    multiple={true}
                                  />
                                )}
                              </Stack>

                              {/* Remove button */}
                              <Tooltip label="Remove team">
                                <ActionIcon
                                  size="sm"
                                  color="red"
                                  variant="subtle"
                                  onClick={() => handleRemoveRow(row.draftId)}
                                  style={{ marginTop: 4 }}
                                  disabled={
                                    isTrailingEmpty && draftRows.length === 1
                                  }
                                >
                                  <AppIcon iconName="X" size={12} />
                                </ActionIcon>
                              </Tooltip>
                            </Group>
                          </Paper>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Stack>
              )}
            </Droppable>
          </DragDropContext>

          {/* ── Modal actions ── */}
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              leftSection={<AppIcon iconName="Check" size={16} />}
              onClick={handleConfirm}
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
};